<template>
    <div class="contact-languages-root">
        <p
            v-if="visibleLanguages.length"
            class="text-sm font-bold text-green-100 mb-3"
            aria-live="polite"
        >
            {{ countLabel }}
        </p>
        <div class="flex flex-wrap gap-4">
            <a
                v-for="lang in visibleSlice"
                :key="lang.code"
                :href="lang.path || '#'"
                class="flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-full border border-white/20 hover:bg-white/20 transition-colors no-underline text-white"
                :aria-label="lang.name || lang.code"
            >
                <img
                    v-if="lang.flag"
                    :alt="lang.name || lang.code"
                    :src="flagSrc(lang.flag)"
                    width="20"
                    height="20"
                    class="rounded-sm object-cover flex-shrink-0"
                />
                <span
                    v-else
                    class="h-5 w-5 rounded-full bg-white/20 flex items-center justify-center text-[10px] font-bold flex-shrink-0"
                >
                    {{ (lang.code || '').substring(0, 2).toUpperCase() }}
                </span>
                <span class="font-bold text-xs uppercase tracking-wide">
                    {{ (lang.code || '').toUpperCase() }}
                </span>
            </a>

            <button
                v-if="!showAll && extraCount > 0"
                type="button"
                class="flex items-center justify-center w-8 h-8 rounded-full bg-white/10 font-bold text-xs hover:bg-white/20 transition-colors text-white"
                @click="showAll = true"
            >
                +{{ extraCount }}
            </button>

            <button
                v-if="showAll && extraCount > 0"
                type="button"
                class="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 font-bold text-xs hover:bg-white/20 transition-colors border border-white/20 text-white"
                @click="showAll = false"
            >
                {{ lessLabel }}
            </button>
        </div>
    </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import ApiClient from '../core/ApiClient.js';
import { flagSrc } from '../utils/flagSrc.js';

defineOptions({ name: 'ContactLanguages' });

/** Same envelope parsing as HeaderLanguageSelector (`POST /api` with `path: '/languages'`). */
function normalizeLanguagesResponse(res) {
    const body = res?.data;
    if (Array.isArray(body?.data)) return body.data;
    if (Array.isArray(body)) return body;
    return [];
}

/**
 * Same merge as HeaderLanguageSelector.visibleLanguages: only locales that have a path
 * for the current page (`paths_by_language`), ordered like the header dropdown.
 */
function buildVisibleLanguages(paths, langs) {
    const pathsObj = paths && typeof paths === 'object' ? paths : {};
    const list = Array.isArray(langs) ? langs : [];

    const langMap = Object.fromEntries(list.map((l) => [l.code, l]));
    const langByUpper = Object.fromEntries(
        list
            .filter((l) => l && l.code != null && l.code !== '')
            .map((l) => [String(l.code).toUpperCase(), l])
    );

    return Object.entries(pathsObj)
        .map(([code, path]) => {
            const lang =
                langMap[code]
                ?? langByUpper[String(code).toUpperCase()];

            return {
                ...(lang || {}),
                code,
                path,
                name:
                    lang?.translated?.name
                    ?? lang?.code_view
                    ?? code,
            };
        })
        .sort((a, b) => (Number(a.index) || 0) - (Number(b.index) || 0));
}

const store = useStore();
const showAll = ref(false);
const languagesFromApi = ref(null);

const texts = computed(() => store.getters.texts || {});

const pathsByLanguage = computed(() => store.state.page?.paths_by_language ?? {});

const resolvedLanguages = computed(() => {
    const fromApi = languagesFromApi.value;
    if (Array.isArray(fromApi) && fromApi.length) return fromApi;
    return store.getters.languages || [];
});

const visibleLanguages = computed(() =>
    buildVisibleLanguages(pathsByLanguage.value, resolvedLanguages.value)
);

const INITIAL_VISIBLE = 6;

const visibleSlice = computed(() => {
    const list = visibleLanguages.value;
    if (showAll.value) return list;
    return list.slice(0, INITIAL_VISIBLE);
});

const extraCount = computed(() => {
    const total = visibleLanguages.value.length;
    return total > INITIAL_VISIBLE ? total - INITIAL_VISIBLE : 0;
});

const countLabel = computed(() => {
    const n = visibleLanguages.value.length;
    const tpl = texts.value['contact-languages-count-label'];
    if (typeof tpl === 'string' && tpl.includes('{n}')) {
        return tpl.replace(/\{n\}/g, String(n));
    }
    if (typeof tpl === 'string' && tpl.trim()) return tpl;
    return n === 1 ? '1 language' : `${n} languages`;
});

const lessLabel = computed(() =>
    texts.value['contact-languages-less'] ?? 'Less'
);

async function fetchLanguages() {
    try {
        const api = new ApiClient();
        const res = await api.request({ path: '/languages' });
        const list = normalizeLanguagesResponse(res);
        if (list.length) {
            languagesFromApi.value = list.map((lang) => (
                lang?.flag ? { ...lang, flag: flagSrc(lang.flag) } : lang
            ));
        }
    } catch {
        /* keep Vuex SSR languages */
    }
}

onMounted(() => {
    fetchLanguages();
});

watch(
    () => store.state.lang?.id,
    (next, prev) => {
        if (prev != null && next != null && next !== prev) {
            showAll.value = false;
            fetchLanguages();
        }
    }
);

watch(visibleLanguages, () => {
    showAll.value = false;
});
</script>

<style scoped>
</style>
